import {
  useApiUrl,
  useCreate,
  useDataProvider,
  useNavigation,
  useNotification,
  useUpdate,
} from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { notifyMutationError } from "@/lib/repository-utils";
import { Sentry } from "@/lib/sentry";
import { parseRead } from "@/lib/zod/read-schema";
import { VenueViewType, venueViewSchema } from "@/pages/venues/schemas/venue";
import { FactsPayload } from "@/pages/venues/schemas/venue-facts";
import { VenueEnrichment } from "@/pages/venues/schemas/venue.aienrichment.form";
import { CreateVenueFormType } from "@/pages/venues/schemas/venue.create.form";
import { VenueEditFormType } from "@/pages/venues/schemas/venue.edit.form";
import placesApiProvider from "@/providers/places-api-provider";

export interface VenueSummary {
  id: string;
  name: string;
  address: string;
  area?: string | null;
  geodata?: Record<string, unknown> | null;
  type?: string | null;
  media: { photos?: Array<{ url: string }> };
  mapsUrl?: string | null;
}

export const useVenueRepository = () => {
  const apiUrl = useApiUrl();
  const { custom, getOne, getList } = useDataProvider()();
  const { open } = useNotification();
  const { mutateAsync } = useCreate({ resource: "venues" });
  const { mutateAsync: updateAsync } = useUpdate({ resource: "venues" });
  const { edit } = useNavigation();

  async function get(id: string): Promise<VenueViewType> {
    const response = await getOne({ id, resource: "venues" });
    // Lenient read + raw fallback — see `@/lib/zod/read-schema`.
    return parseRead(venueViewSchema, response, "venue");
  }

  async function generateId(): Promise<string> {
    const result = await custom?.({
      url: ``,
      meta: {
        urlPath: "venues/id",
      },
      method: "get",
    });
    if (!result?.data) {
      open?.({
        message: "ID generation failed. Please try again later!",
        type: "error",
      });
      throw new Error("ID Generation failure");
    }

    return result?.data?.toString()!;
  }

  async function create(values: CreateVenueFormType): Promise<void> {
    Sentry.addBreadcrumb({
      category: "venues.create",
      message: "Creating venue",
      level: "info",
      data: { name: values.name },
    });
    await mutateAsync?.(
      { values },
      {
        onSuccess: (data) => {
          console.debug("Successful venue creation", data);
          const id = (data as any).id;
          Sentry.addBreadcrumb({
            category: "venues.create",
            message: "Venue created successfully",
            level: "info",
            data: { id, name: values.name },
          });
          edit("venues", id, "replace");
          open?.({
            type: "success",
            message: "Successfully created venue",
            description: `You created a venue called ${values.name}`,
          });
        },
        onError: (error) => {
          Sentry.addBreadcrumb({
            category: "venues.create",
            message: "Failed to create venue",
            level: "error",
            data: { name: values.name },
          });
          Sentry.captureException(error);
          console.error("Failed to create venue with values", values, error);
          notifyMutationError(open, "Create venue request", error);
        },
      },
    );
  }
  async function update(
    values: Partial<
      Omit<VenueEditFormType, "openingHours" | "accessibility">
    > & { hasPlaceholderArt?: boolean } & FactsPayload,
  ) {
    Sentry.addBreadcrumb({
      category: "venues.update",
      message: "Updating venue",
      level: "info",
      data: { id: values.id, name: values.name },
    });
    const response = await updateAsync?.(
      { id: values.id!, values },
      {
        onSuccess: (data) => {
          console.debug("Successful venue update", data);
          Sentry.addBreadcrumb({
            category: "venues.update",
            message: "Venue updated successfully",
            level: "info",
            data: { id: values.id },
          });
          edit("venues", values.id!, "replace");
          open?.({
            type: "success",
            message: "Successfully updated venue",
            description: `You updated a venue called ${(data as any).name}`,
          });
        },
        onError: (error) => {
          Sentry.addBreadcrumb({
            category: "venues.update",
            message: "Failed to update venue",
            level: "error",
            data: { id: values.id },
          });
          Sentry.captureException(error);
          console.error("Failed to update venue with values", values, error);
          notifyMutationError(open, "Update venue request", error);
        },
      },
    );

    return response.data.id?.toString()!;
  }

  async function search(query: string): Promise<any[]> {
    if (!query) {
      return [];
    }

    // Strip standalone hyphens to prevent Typesense negation operator
    const sanitizedQuery = query.replace(/\s-\s/g, " ");

    const response = await getList({
      resource: "venues",
      pagination: {
        currentPage: 0,
        pageSize: 10,
      },
      filters: [
        {
          field: "hide",
          operator: "eq",
          value: false,
        },
      ],
      meta: {
        sortBy: "_text_match:desc",
        query: sanitizedQuery,
        queryBy:
          "enrichment.transliterations.el,enrichment.transliterations.en,name",
      },
    });

    return response.data.map((item: any) => item.document);
  }

  async function searchPlace(query: string): Promise<any[]> {
    try {
      const response = await placesApiProvider.post(
        "/places:searchText",
        {
          textQuery: query,
          languageCode: "en",
        },
        {
          headers: {
            "X-Goog-FieldMask": "*",
          },
        },
      );
      console.debug("Places API Response", response?.data.places);
      return response?.data?.places as any[];
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to search for google maps places",
        description: toHumanErrorMessage(error),
      });
      return [] as any[];
    }
  }

  async function triggerEnrich(
    id: string,
    params: {
      instagramPageUrl: string;
      instagramScreenshot?: string;
      place?: any;
      updateMapsData?: boolean;
    },
  ): Promise<boolean> {
    Sentry.addBreadcrumb({
      category: "venues.enrich",
      message: "Triggering venue enrichment",
      level: "info",
      data: { id },
    });
    try {
      await custom?.({
        url: `${apiUrl}/venues/enrich`,
        method: "post",
        payload: {
          id,
          ...params,
          mapsPlaceDetails: params.place,
          updateMapsData: params.updateMapsData ?? false,
          existingSources: [
            {
              id: params.place.id,
              source: "places.googleapis.com/v1",
              url: params.place.googleMapsUri,
              data: params.place,
              createdAt: new Date().toISOString(),
              createdBy: "ims",
            },
          ],
        },
      });
      return true;
    } catch (error) {
      Sentry.addBreadcrumb({
        category: "venues.enrich",
        message: "Failed to trigger venue enrichment",
        level: "error",
        data: { id },
      });
      Sentry.captureException(error);
      open?.({
        type: "error",
        message: "Failed to trigger enrichment",
        description: toHumanErrorMessage(error),
      });
      return false;
    }
  }

  async function updateEnrichment(
    id: string,
    enrichment: VenueEnrichment,
  ): Promise<boolean> {
    try {
      await custom?.({
        url: `${apiUrl}/venues/enrich`,
        method: "put",
        payload: { id, enrichment },
      });
      open?.({
        type: "success",
        message: "Enrichment approved",
        description: "The enrichment data has been saved successfully.",
      });
      return true;
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to save enrichment",
        description: toHumanErrorMessage(error),
      });
      return false;
    }
  }

  async function nearestNeighbours(
    embedding: number[],
    limit: number,
  ): Promise<VenueSummary[]> {
    const response = await custom?.({
      url: `${apiUrl}/search/nearest-neighbours-by-embedding`,
      method: "post",
      payload: { Embedding: embedding, TargetType: "venue", Limit: limit },
    });
    return (response?.data as VenueSummary[]) ?? [];
  }

  async function getUsageCount(id: string): Promise<{ events: number }> {
    const eventsResult = await getList?.({
      resource: "events",
      pagination: {
        pageSize: 1,
        currentPage: 0,
      },
      filters: [
        {
          field: "venue.id",
          operator: "eq",
          value: id,
        },
      ],
      meta: {
        includeFields: ["id"],
      },
    });

    return { events: eventsResult?.total || 0 };
  }

  async function merge(sourceId: string, targetId: string) {
    try {
      const response = await custom?.({
        url: `${apiUrl}/venues/merge`,
        method: "post",
        payload: {
          sourceVenueId: sourceId,
          targetVenueId: targetId,
        },
      });

      open?.({
        type: "success",
        message: "Successfully merged venues",
        description: (response?.data?.errors ?? []).join("\n"),
      });
    } catch (e: any) {
      console.error(e);
      open?.({
        type: "error",
        message: "Failed to merge venue",
        description: e?.response?.data?.detail,
      });
      throw e;
    }
  }

  return {
    get,
    generateId,
    create,
    update,
    search,
    searchPlace,
    triggerEnrich,
    updateEnrichment,
    nearestNeighbours,
    getUsageCount,
    merge,
  };
};
